import { UnknownError, ValueOf } from '@omnea-digital/toolbox'
import { UseQueryResult, useQuery, useSuspenseQuery } from '@tanstack/react-query'
import isNil from 'lodash/isNil'
import {
  Address,
  Currency,
  Department,
  LineItemType,
  PaymentMethod,
  PaymentTerm,
  Subsidiary,
  TaxType,
} from '@omnea-app/core-data/coreDataFrontendClient'
import { CoreDataHttpClient } from '@omnea-app/core-data/coreDataHttpClient'
import { QUERY_TIME_CONFIG } from '@omnea-app/fe-api-client/queryConfig'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { CamelSystemCoreDataType, formatEntityType } from '@omnea-app/packages/universal/core-data/formatDependencyDataType'
import { throwIfApiError } from '@omnea-app/packages/universal/error-handling/throwifApiError'
import { GridFilterModel } from '@omnea-app/packages/universal/tables/gridModel'

export type CamelCoreDataOptionMap = {
  address: Address
  currency: Currency
  department: Department
  lineItemType: LineItemType
  paymentMethod: PaymentMethod
  paymentTerm: PaymentTerm
  subsidiary: Subsidiary
  taxType: TaxType
}
export type SystemCoreDataOption = ValueOf<CamelCoreDataOptionMap>

const systemCoreDataQueryKey = 'systemCoreData'
const filteredSystemCoreDataQueryKey = 'filteredSystemCoreData'

// Sort `isAnyOf` ids so equivalent filters hit the same react-query cache key.
const serializeFilterModel = (filterModel: GridFilterModel) => ({
  logicOperator: filterModel.logicOperator,
  items: filterModel.items.map((item) => {
    const value = 'value' in item ? item.value : undefined
    return {
      field: item.field,
      operator: item.operator,
      value: Array.isArray(value) ? [...value].sort() : value,
    }
  }),
})

export const getSystemCoreDataQueryKey = (
  type: CamelSystemCoreDataType | null,
  includeDependenciesMetadata?: boolean,
  includeArchived?: boolean
) => [systemCoreDataQueryKey, String(type), includeDependenciesMetadata ?? false, includeArchived ?? false]

export const getFilteredSystemCoreDataQueryKey = (
  type: CamelSystemCoreDataType,
  filterModel: GridFilterModel,
  includeDependenciesMetadata?: boolean,
  includeArchived?: boolean
) => [
  filteredSystemCoreDataQueryKey,
  type,
  includeDependenciesMetadata ?? false,
  includeArchived ?? false,
  serializeFilterModel(filterModel),
]

// Prefix used by mutation hooks to invalidate all filtered queries for a type
// regardless of which `filterModel` / flags are cached.
export const getFilteredSystemCoreDataInvalidationKey = (type: CamelSystemCoreDataType) => [filteredSystemCoreDataQueryKey, type]

// Prefix used to invalidate every unfiltered query for a type regardless of the
// `includeDependenciesMetadata` / `includeArchived` flags cached — the full key pins both to their
// defaults, so it would otherwise miss the metadata/archived variants the UI actually reads.
export const getSystemCoreDataInvalidationKey = (type: CamelSystemCoreDataType) => [systemCoreDataQueryKey, String(type)]

const readItems = <T extends CamelSystemCoreDataType>(data: unknown, coreDataType: T): CamelCoreDataOptionMap[T][] => {
  const responseKey = formatEntityType(coreDataType, 'camelPlural')
  const items = (data as Record<string, unknown>)[responseKey]
  if (!Array.isArray(items)) throw new UnknownError({ message: 'Invalid response shape' })
  return items as CamelCoreDataOptionMap[T][]
}

export const fetchSystemCoreData = async <T extends CamelSystemCoreDataType>({
  getToken,
  coreDataType,
  includeDependenciesMetadata = false,
  includeArchived = false,
}: {
  getToken: () => Promise<string>
  coreDataType: T
  includeDependenciesMetadata?: boolean
  includeArchived?: boolean
}): Promise<CamelCoreDataOptionMap[T][]> => {
  const authorization = await getToken()
  const coreDataClient = new CoreDataHttpClient({ authorization })
  const entityTypeKebab = formatEntityType(coreDataType, 'kebab')

  const { data, error } = await coreDataClient.GET('/system-core-data/{entityType}', {
    params: { path: { entityType: entityTypeKebab }, query: { includeDependenciesMetadata, includeArchived } },
  })
  throwIfApiError(error)
  return readItems(data, coreDataType)
}

const fetchFilteredSystemCoreData = async <T extends CamelSystemCoreDataType>({
  getToken,
  coreDataType,
  filterModel,
  includeDependenciesMetadata,
  includeArchived,
}: {
  getToken: () => Promise<string>
  coreDataType: T
  filterModel: GridFilterModel
  includeDependenciesMetadata: boolean
  includeArchived: boolean
}): Promise<CamelCoreDataOptionMap[T][]> => {
  const authorization = await getToken()
  const coreDataClient = new CoreDataHttpClient({ authorization })
  const entityTypeKebab = formatEntityType(coreDataType, 'kebab')

  const { data, error } = await coreDataClient.POST('/system-core-data/{entityType}/list', {
    params: { path: { entityType: entityTypeKebab } },
    body: { includeDependenciesMetadata, includeArchived, filterModel },
  })
  throwIfApiError(error)
  return readItems(data, coreDataType)
}

// Overloaded function signatures for better type inference
export function useSystemCoreData<Type extends CamelSystemCoreDataType>({
  coreDataType,
  includeDependenciesMetadata,
  includeArchived,
  enabled,
  staleTime,
}: {
  coreDataType: Type
  includeDependenciesMetadata?: boolean
  includeArchived?: boolean
  enabled?: boolean
  staleTime?: number
}): UseQueryResult<CamelCoreDataOptionMap[Type][], Error>
export function useSystemCoreData({
  coreDataType,
  includeDependenciesMetadata,
  includeArchived,
  enabled,
  staleTime,
}: {
  coreDataType: null
  includeDependenciesMetadata?: boolean
  includeArchived?: boolean
  enabled?: boolean
  staleTime?: number
}): UseQueryResult<never[], Error>
export function useSystemCoreData<Type extends CamelSystemCoreDataType | null>({
  coreDataType,
  includeDependenciesMetadata,
  includeArchived,
  enabled,
  staleTime,
}: {
  coreDataType: Type
  includeDependenciesMetadata?: boolean
  includeArchived?: boolean
  enabled?: boolean
  staleTime?: number
}): UseQueryResult<Type extends CamelSystemCoreDataType ? CamelCoreDataOptionMap[Type][] : never[], Error>
export function useSystemCoreData<Type extends CamelSystemCoreDataType | null>({
  coreDataType,
  includeDependenciesMetadata = false,
  includeArchived = false,
  enabled = true,
  staleTime,
}: {
  coreDataType: Type
  includeDependenciesMetadata?: boolean
  includeArchived?: boolean
  enabled?: boolean
  staleTime?: number
}) {
  const { getToken } = useAuthenticatedUser()

  return useQuery({
    queryKey: getSystemCoreDataQueryKey(coreDataType, includeDependenciesMetadata, includeArchived),
    queryFn: async () => {
      if (isNil(coreDataType)) return []
      return fetchSystemCoreData({ getToken, coreDataType, includeDependenciesMetadata, includeArchived })
    },
    enabled,
    staleTime: staleTime ?? QUERY_TIME_CONFIG.REFERENCE_DATA_STALE_TIME,
  })
}

export const useFilteredSystemCoreData = <Type extends CamelSystemCoreDataType>({
  coreDataType,
  filterModel,
  includeDependenciesMetadata = false,
  includeArchived = false,
  enabled = true,
  staleTime,
}: {
  coreDataType: Type
  filterModel: GridFilterModel
  includeDependenciesMetadata?: boolean
  includeArchived?: boolean
  enabled?: boolean
  staleTime?: number
}) => {
  const { getToken } = useAuthenticatedUser()

  return useQuery({
    queryKey: getFilteredSystemCoreDataQueryKey(coreDataType, filterModel, includeDependenciesMetadata, includeArchived),
    queryFn: () =>
      fetchFilteredSystemCoreData({ getToken, coreDataType, filterModel, includeDependenciesMetadata, includeArchived }),
    enabled,
    // Retain prior rows during a refetch so applying / clearing a filter doesn't flash to empty;
    // only within the same coreDataType to avoid wrong-type rows on a type switch.
    placeholderData: (previousData, previousQuery) => (previousQuery?.queryKey?.[1] === coreDataType ? previousData : undefined),
    staleTime: staleTime ?? QUERY_TIME_CONFIG.REFERENCE_DATA_STALE_TIME,
  })
}

export const useSystemCoreDataSuspense = <Type extends CamelSystemCoreDataType>({
  coreDataType,
  includeDependenciesMetadata = false,
  includeArchived = false,
}: {
  coreDataType: Type
  includeDependenciesMetadata?: boolean
  includeArchived?: boolean
}): CamelCoreDataOptionMap[Type][] => {
  const { getToken } = useAuthenticatedUser()

  return useSuspenseQuery({
    queryKey: getSystemCoreDataQueryKey(coreDataType, includeDependenciesMetadata, includeArchived),
    queryFn: async () => fetchSystemCoreData({ getToken, coreDataType, includeDependenciesMetadata, includeArchived }),
    staleTime: QUERY_TIME_CONFIG.REFERENCE_DATA_STALE_TIME,
  }).data
}
